import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Shield, TrendingDown, Eye, AlertTriangle, CheckCircle } from 'lucide-react';

const buyBoxFeatures = [
  { icon: Eye, title: '24/7 Monitoring', desc: 'Continuous tracking of Buy Box status across all ASINs' },
  { icon: TrendingDown, title: 'Price Tracking', desc: 'Monitor competitor pricing and MAP violations' },
  { icon: AlertTriangle, title: 'Instant Alerts', desc: 'Get notified the moment you lose the Buy Box' },
  { icon: CheckCircle, title: 'Win Rate Analytics', desc: 'Track Buy Box win rate trends over time' },
];

export default function BuyBoxSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#05060B] py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.08)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div 
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
                Buy Box & Pricing
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Protect your{' '}
                <span className="text-cyan-400 cyan-glow">Buy Box.</span>
              </h2>
              <p className="text-lg text-dark-400 mb-8">
                Monitor Buy Box status, track competitor pricing, and get instant alerts when you're at risk. Know immediately when something changes.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {buyBoxFeatures.map((feature, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-colors"
                  >
                    <feature.icon className="w-6 h-6 text-cyan-400 mb-3" />
                    <h4 className="font-medium text-white mb-1">{feature.title}</h4>
                    <p className="text-sm text-dark-400">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div 
              className={`transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <div className="glass-card p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <div className="font-medium text-white">Buy Box Status</div>
                      <div className="text-sm text-green-400">You own the Buy Box</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-cyan-400">94%</div>
                    <div className="text-xs text-dark-500">Win Rate (7d)</div>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="text-sm text-dark-500 mb-2">Top ASINs Monitored</div>
                  {[
                    { asin: 'B08XYZ1234', status: 'owned', price: '$29.99', competitor: '$31.50' },
                    { asin: 'B08ABC5678', status: 'owned', price: '$45.00', competitor: '$44.99' },
                    { asin: 'B08DEF9012', status: 'lost', price: '$19.99', competitor: '$18.50' },
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-xl border ${
                        item.status === 'owned' 
                          ? 'bg-green-500/5 border-green-500/20' 
                          : 'bg-red-500/5 border-red-500/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${item.status === 'owned' ? 'bg-green-400' : 'bg-red-400'}`} />
                        <span className="text-sm text-white font-mono">{item.asin}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-white">{item.price}</span>
                        <span className="text-dark-500">vs {item.competitor}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-white">Buy Box Lost</div>
                      <div className="text-sm text-dark-400">
                        ASIN B08DEF9012 lost Buy Box to competitor. Price difference: $1.49
                      </div>
                      <div className="text-xs text-dark-500 mt-1">2 minutes ago</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
